import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Tooltip, Typography, Collapse } from '@mui/material';
import { Iconify } from 'src/components';
import {
  contextPanelSx,
  patientHeaderSx,
  patientAvatarSx,
  patientTagSx,
  contextBodySx,
  dataSectionSx,
  dataRowSx,
  notesSectionSx,
} from '../styles';

/**
 * Helper to parse merge tags in text
 */
const parseMergeTags = (text, mergeTagData = {}) => {
  if (!text) return text;
  return text.replace(/\$([a-zA-Z0-9_]+)/g, (match, key) => {
    const value = mergeTagData[key];
    return value !== undefined ? value : match;
  });
};

// Detect whether a card has conditional logic attached.
// Supports both new (metadata.conditionLogic.conditions[]) and legacy
// (form-level conditional_logics[] entry matching the card's section + field id) formats.
const cardHasConditionalLogic = (card, conditionalLogics) => {
  if (!card) return false;
  const newLogic = card?.metadata?.conditionLogic?.conditions;
  if (Array.isArray(newLogic) && newLogic.length > 0) return true;
  if (Array.isArray(conditionalLogics) && conditionalLogics.length > 0) {
    return conditionalLogics.some(
      (cl) => cl?.target_section === card?.originalSectionId
        && cl?.target_field === card?.fieldId
    );
  }
  return false;
};

const ContextPanel = ({
  type = 'inbound',
  scriptCategory,
  patient = {},
  tags = [],
  progress = {},
  collectedData = [],
  notes = '',
  onSectionClick,
  onQuestionClick,
  cards = [],
  currentCardIndex = 0,
  completedCards = new Set(),
  skippedCards = new Set(),
  isOpen = true,
  className = '',
  mergeTagData = {},
  conditionalLogics = [],
  showConditionalIndicators = false,
}) => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Get questions for a specific section
  const getQuestionsForSection = (sectionId) =>
    cards
      .map((card, index) => ({ ...card, cardIndex: index }))
      .filter((card) => card.section === sectionId);
  const { name = 'Unknown Caller', initials = '?', status = '' } = patient;
  const isUnknown = initials === '?';

  return (
    <Box sx={contextPanelSx} className={`${className} ${isOpen ? 'open' : ''}`}>
      {/* Patient Header */}
      <Box sx={patientHeaderSx(type)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', mb: '14px' }}>
          <Box sx={patientAvatarSx(type, isUnknown)}>
            {initials}
          </Box>
          <Box>
            <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#212B36' }}>
              {name}
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#637381', mt: '2px' }}>
              {status}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Box sx={patientTagSx(type === 'inbound' || scriptCategory === 'TCO' ? 'type-in' : 'type-out')}>
            {(() => {
              if (scriptCategory === 'TCO') return <Iconify icon="mdi:clipboard-text-outline" width={14} height={14} />;
              if (type === 'inbound') return <Iconify icon="mdi:call-received" width={14} height={14} />;
              return <Iconify icon="mdi:call-made" width={14} height={14} />;
            })()}
            {(() => { if (scriptCategory === 'TCO') return 'TCO'; if (type === 'inbound') return 'Inbound'; return 'Outbound'; })()}
          </Box>
          {tags.map((tag, index) => (
            <Box key={index} sx={patientTagSx(tag.variant || 'treatment')}>
              {tag.icon || <Iconify icon="mdi:hospital" width={14} height={14} />}
              {tag.label}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Context Body */}
      <Box sx={contextBodySx}>
        {/* Progress Section - Styled from HTML sample */}
        <Box
          sx={{
            bgcolor: '#F5F5F5',
            border: '1px solid #EEEEEE',
            borderRadius: '12px',
            padding: { xs: '12px', sm: '16px' },
            mb: '20px',
          }}
        >
          {/* Header with skip count */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '14px' }}>
            <Typography
              sx={{
                fontSize: { xs: '12px', sm: '13px' },
                fontWeight: 600,
                color: '#212B36',
              }}
            >
              Script Progress
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Typography
                sx={{
                  fontSize: { xs: '11px', sm: '12px' },
                  color: '#637381',
                }}
              >
                {progress.current || 1} of {progress.total || 7}
              </Typography>
              {(() => {
                const skippedCount = progress.sections?.filter(s => s.isSkipped).length || 0;
                if (skippedCount > 0) {
                  return (
                    <Typography
                      sx={{
                        fontSize: { xs: '11px', sm: '12px' },
                        color: '#B76E00',
                        fontWeight: 500,
                      }}
                    >
                      ({skippedCount} skipped)
                    </Typography>
                  );
                }
                return null;
              })()}
            </Box>
          </Box>

          {progress.sections?.map((section, index, arr) => {
            // Closure section is the last section and has no cards/questions
            const isClosureSection = index === arr.length - 1 && getQuestionsForSection(section.id).length === 0;
            const sectionQuestions = isClosureSection ? [] : getQuestionsForSection(section.id);
            const isExpanded = expandedSections[section.id] ?? true;
            const hasQuestions = sectionQuestions.length > 0;
            const sectionNumber = index + 1;

            return (
              <Box key={section.id || index}>
                {/* Section Header - progress-item styling */}
                <Box
                  onClick={() => {
                    if (isClosureSection) {
                      onSectionClick?.(section.id);
                    } else {
                      onSectionClick?.(section.id);
                      toggleSection(section.id);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: '10px', sm: '12px' },
                    padding: { xs: '8px 10px', sm: '10px 12px' },
                    margin: { xs: '4px -10px', sm: '4px -12px' },
                    borderRadius: '8px',
                    cursor: 'pointer',
                    bgcolor: section.isActive ? 'rgba(0,167,111,0.08)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                    transition: 'all 0.15s ease',
                  }}
                >
                  {/* Section Number Circle - progress-dot styling (26px) */}
                  <Box
                    sx={{
                      width: { xs: '24px', sm: '26px' },
                      height: { xs: '24px', sm: '26px' },
                      minWidth: { xs: '24px', sm: '26px' },
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: { xs: '10px', sm: '11px' },
                      fontWeight: 600,
                      flexShrink: 0,
                      bgcolor: (() => {
                        if (section.isSkipped && !section.isActive) return '#FFAB00';
                        if (section.isActive) return '#00A76F';
                        if (section.isCompleted) return '#22C55E';
                        return '#EEEEEE';
                      })(),
                      color: (() => {
                        if (section.isActive || section.isCompleted || (section.isSkipped && !section.isActive)) return '#fff';
                        return '#637381';
                      })(),
                      transform: section.isActive ? 'scale(1.1)' : 'scale(1)',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {(() => {
                      if (isClosureSection) return <Iconify icon="mdi:flag-checkered" width={14} height={14} />;
                      if (section.isCompleted) return <Iconify icon="mdi:check" width={12} height={12} />;
                      if (section.isSkipped && !section.isActive) return <Iconify icon="mdi:alert" width={12} height={12} />;
                      return sectionNumber;
                    })()}
                  </Box>

                  {/* Section Name - progress-text styling (13px) */}
                  <Typography
                    sx={{
                      flex: 1,
                      fontSize: { xs: '12px', sm: '13px' },
                      color: section.isActive ? '#212B36' : '#637381',
                      fontWeight: section.isActive ? 500 : 400,
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {section.name}
                  </Typography>

                  {/* Status Badge - progress-status styling (10px font, 2px 8px padding) */}
                  {section.isSkipped && !section.isActive && (
                    <Box
                      sx={{
                        fontSize: { xs: '9px', sm: '10px' },
                        fontWeight: 500,
                        padding: '2px 8px',
                        borderRadius: '10px',
                        bgcolor: 'rgba(255,171,0,0.12)',
                        color: '#B76E00',
                      }}
                    >
                      Skipped
                    </Box>
                  )}

                  {/* Expand/Collapse Icon */}
                  {hasQuestions && (
                    <Iconify
                      icon={isExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                      width={18}
                      height={18}
                      sx={{ color: '#919EAB', flexShrink: 0 }}
                    />
                  )}
                </Box>

                {/* Questions List - Sub-items with section.question numbering */}
                {hasQuestions && (
                  <Collapse in={isExpanded}>
                    <Box sx={{ pb: '4px' }}>
                      {sectionQuestions.map((question, qIndex) => {
                        const isCurrentQuestion = question.cardIndex === currentCardIndex;
                        const isCompleted = completedCards.has(question.cardIndex);
                        const isSkipped = skippedCards.has(question.cardIndex);
                        const hasConditionalLogic = showConditionalIndicators
                          && cardHasConditionalLogic(question, conditionalLogics);
                        // Format as "section.question" (e.g., 2.1, 2.3)
                        const questionNumber = `${sectionNumber}.${qIndex + 1}`;

                        return (
                          <Box
                            key={question.cardIndex}
                            onClick={() => onQuestionClick?.(question.cardIndex)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: { xs: '8px', sm: '10px' },
                              // Sub-item: padding-left 28px from sample
                              padding: { xs: '6px 10px 6px 24px', sm: '8px 12px 8px 28px' },
                              margin: '2px 0',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              bgcolor: (() => {
                                if (isCurrentQuestion) return 'rgba(0,167,111,0.08)';
                                if (hasConditionalLogic) return 'rgba(118,53,220,0.08)';
                                return 'transparent';
                              })(),
                              borderLeft: hasConditionalLogic && !isCurrentQuestion
                                ? '3px solid #7635DC'
                                : '3px solid transparent',
                              '&:hover': { bgcolor: hasConditionalLogic ? 'rgba(118,53,220,0.14)' : 'rgba(0,0,0,0.04)' },
                              transition: 'all 0.15s ease',
                            }}
                          >
                            {/* Question Number Circle - Sub dot (22px from sample) */}
                            <Box
                              sx={{
                                position: 'relative',
                                width: { xs: '20px', sm: '22px' },
                                height: { xs: '20px', sm: '22px' },
                                minWidth: { xs: '20px', sm: '22px' },
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                // Sub-item font: 9px from sample
                                fontSize: { xs: '8px', sm: '9px' },
                                fontWeight: 600,
                                flexShrink: 0,
                                bgcolor: (() => {
                                  if (isCurrentQuestion) return '#00A76F';
                                  if (isCompleted) return '#22C55E';
                                  if (isSkipped) return '#FFF5CC';
                                  if (hasConditionalLogic) return '#EBD6FD';
                                  return '#EEEEEE';
                                })(),
                                color: (() => {
                                  if (isCurrentQuestion || isCompleted) return '#fff';
                                  if (isSkipped) return '#B76E00';
                                  if (hasConditionalLogic) return '#431A9E';
                                  return '#637381';
                                })(),
                                border: hasConditionalLogic && !isCurrentQuestion && !isCompleted
                                  ? '1.5px solid #7635DC'
                                  : 'none',
                                transform: isCurrentQuestion ? 'scale(1.1)' : 'scale(1)',
                                transition: 'all 0.15s ease',
                              }}
                            >
                              {(() => {
                                if (isCompleted) return <Iconify icon="mdi:check" width={10} height={10} />;
                                if (isSkipped) return '↷';
                                return questionNumber;
                              })()}
                            </Box>

                            {/* Question Title - Sub-item text (11px from sample) */}
                            <Typography
                              sx={{
                                flex: 1,
                                fontSize: { xs: '10px', sm: '11px' },
                                color: isCurrentQuestion ? '#212B36' : '#637381',
                                fontWeight: isCurrentQuestion ? 500 : 400,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                lineHeight: 1.4,
                              }}
                            >
                              {parseMergeTags(question.title, mergeTagData) || `Question ${qIndex + 1}`}
                            </Typography>

                            {/* Conditional Logic indicator chip */}
                            {hasConditionalLogic && (
                              <Tooltip arrow placement="top" title="Has conditional logic">
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: { xs: '9px', sm: '10px' },
                                    fontWeight: 600,
                                    padding: '2px 6px',
                                    borderRadius: '8px',
                                    bgcolor: 'rgba(118,53,220,0.16)',
                                    color: '#431A9E',
                                    flexShrink: 0,
                                    lineHeight: 1,
                                    letterSpacing: '0.2px',
                                  }}
                                >
                                  <Iconify icon="mdi:source-branch" width={10} height={10} />
                                  Conditional
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Collected Data Section */}
        {collectedData.length > 0 && (
          <Box sx={dataSectionSx}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: '#637381',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: '10px',
              }}
            >
              Collected Data
            </Typography>
            {collectedData.map((item, index) => (
              <Box key={index} sx={dataRowSx}>
                <Typography sx={{ flex: '0 0 80px', fontSize: 12, color: '#637381' }}>
                  {item.label}
                </Typography>
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: 13,
                    fontWeight: item.value ? 500 : 400,
                    color: item.value ? '#212B36' : '#919EAB',
                    fontStyle: item.value ? 'normal' : 'italic',
                  }}
                >
                  {item.value || 'Not provided'}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Notes Section */}
        <Box sx={notesSectionSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '10px' }}>
            <Iconify icon="mdi:note-edit-outline" width={18} height={18} sx={{ color: '#FFAB00' }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#212B36' }}>
              Call Notes
            </Typography>
          </Box>
          {/* Display formatted notes as HTML */}
          {notes && (
            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: '8px',
                border: '1px solid #E0E0E0',
                p: 1.5,
                mb: 1,
                fontSize: 12,
                maxHeight: '200px',
                overflowY: 'auto',
                '& strong': { fontWeight: 600 },
                '& div': { lineHeight: 1.5 },
              }}
              dangerouslySetInnerHTML={{ __html: notes }}
            />
          )}
          {!notes && (
            <Typography sx={{ fontSize: 12, color: '#919EAB', fontStyle: 'italic' }}>
              No notes added yet. Use the &quot;Add a note&quot; button on each question card.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

ContextPanel.propTypes = {
  type: PropTypes.oneOf(['inbound', 'outbound']),
  scriptCategory: PropTypes.string,
  patient: PropTypes.shape({
    name: PropTypes.string,
    initials: PropTypes.string,
    status: PropTypes.string,
  }),
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      variant: PropTypes.string,
      icon: PropTypes.node,
    })
  ),
  progress: PropTypes.shape({
    current: PropTypes.number,
    total: PropTypes.number,
    sections: PropTypes.array,
  }),
  collectedData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string,
    })
  ),
  notes: PropTypes.string,
  onSectionClick: PropTypes.func,
  onQuestionClick: PropTypes.func,
  cards: PropTypes.array,
  currentCardIndex: PropTypes.number,
  completedCards: PropTypes.instanceOf(Set),
  skippedCards: PropTypes.instanceOf(Set),
  isOpen: PropTypes.bool,
  className: PropTypes.string,
  mergeTagData: PropTypes.object,
  conditionalLogics: PropTypes.array,
  showConditionalIndicators: PropTypes.bool,
};

export default ContextPanel;
